import { bbox } from "@turf/bbox";
import { hexGrid } from "@turf/hex-grid";
import { pointGrid } from "@turf/point-grid";
import { distance } from "@turf/distance";
import { centroid } from "@turf/centroid";
import { squareGrid } from "@turf/square-grid";
import { triangleGrid } from "@turf/triangle-grid";
import { clone } from "@turf/clone";
import { featureCollection, Grid, Units, validateBBox } from "@turf/helpers";
import { featureEach } from "@turf/meta";
import { collectionOf } from "@turf/invariant";
import { BBox, Feature, FeatureCollection, Point, Polygon } from "geojson";

/**
 * Takes a set of points and estimates their 'property' values on a grid using the [Inverse Distance Weighting (IDW) method](https://en.wikipedia.org/wiki/Inverse_distance_weighting).
 *
 * @function
 * @param {FeatureCollection<Point>} points with known value
 * @param {number} cellSize the distance across each grid point
 * @param {Object} [options={}] Optional parameters
 * @param {string} [options.gridType='square'] defines the output format based on a Grid Type (options: 'square' | 'point' | 'hex' | 'triangle')
 * @param {string} [options.property='elevation'] the property name in `points` from which z-values will be pulled, zValue fallbacks to 3rd coordinate if no property exists.
 * @param {Units} [options.units='kilometers'] used in calculating cellSize. Supports all valid Turf {@link https://turfjs.org/docs/api/types/Units Units}.
 * @param {number} [options.weight=1] exponent regulating the distance-decay weighting
 * @param {BBox}   [options.bbox=bbox(points)] Bounding Box Array [west, south, east, north] associated with the FeatureCollection.
 * @returns {FeatureCollection<Point|Polygon>} grid of points or polygons with interpolated 'property'
 * @example
 * var points = turf.randomPoint(30, {bbox: [50, 30, 70, 50]});
 *
 * // add a random property to each point
 * turf.featureEach(points, function(point) {
 *     point.properties.solRad = Math.random() * 50;
 * });
 * var options = {gridType: 'points', property: 'solRad', units: 'miles'};
 * var grid = turf.interpolate(points, 100, options);
 *
 * //addToMap
 * var addToMap = [grid];
 */
function interpolate<T extends Grid = "square">(
  points: FeatureCollection<Point>,
  cellSize: number,
  options?: {
    gridType?: T;
    property?: string;
    units?: Units;
    weight?: number;
    bbox?: BBox;
  }
): FeatureCollection<T extends "point" ? Point : Polygon> {
  // Optional parameters
  options = options || {};

  // Validation pre-options parsing
  if (typeof options !== "object") {
    throw new Error("options is invalid");
  }
  if (!points) {
    throw new Error("points is required");
  }
  collectionOf(points, "Point", "input must contain Points");
  if (!cellSize) {
    throw new Error("cellSize is required");
  }

  var gridType = options.gridType ?? "square";
  var property = options.property ?? "elevation";
  var weight = options.weight ?? 1;
  var box = options.bbox ?? bbox(points);

  // validation post options-parsing
  if (weight !== undefined && typeof weight !== "number") {
    throw new Error("weight must be a number");
  }

  validateBBox(box);

  var grid;
  switch (gridType) {
    case "point":
    case "points":
      grid = pointGrid(box, cellSize, options);
      break;
    case "square":
    case "squares":
      grid = squareGrid(box, cellSize, options);
      break;
    case "hex":
    case "hexes":
      grid = hexGrid(box, cellSize, options);
      break;
    case "triangle":
    case "triangles":
      grid = triangleGrid(box, cellSize, options);
      break;
    default:
      throw new Error("invalid gridType");
  }
  var results: Feature<Point | Polygon>[] = [];
  featureEach<Point | Polygon>(grid, function (gridFeature) {
    var zw = 0;
    var sw = 0;
    // calculate the distance from each input point to the grid points
    featureEach(points, function (point) {
      var gridPoint =
        gridType === "point"
          ? (gridFeature as Feature<Point>)
          : centroid(gridFeature);
      var d = distance(gridPoint, point, options);
      var zValue;
      // property has priority for zValue, fallbacks to 3rd coordinate from geometry
      if (property !== undefined) {
        zValue = point.properties?.[property];
      }
      if (zValue === undefined) {
        zValue = point.geometry.coordinates[2];
      }
      if (zValue === undefined) {
        throw new Error("zValue is missing");
      }
      if (d === 0) {
        zw = zValue;
      }
      var w = 1.0 / Math.pow(d, weight);
      sw += w;
      zw += w * zValue;
    });
    // write interpolated value for each grid point
    var newFeature = clone(gridFeature);
    newFeature.properties ??= {};
    newFeature.properties[property] = zw / sw;
    results.push(newFeature);
  });
  return featureCollection(results) as FeatureCollection<
    T extends "point" ? Point : Polygon
  >;
}

export { interpolate };
export default interpolate;
